import { Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";

export const Footer = ({ settings }) => {
  const { t } = useLang();
  const { pathname } = useLocation();
  const base = pathname === "/" ? "" : "/";
  const email = settings?.contact_email || "info@findivine.org";
  const phone = settings?.phone || "+91-8875285537";
  const social = settings?.social || {};
  const socials = [
    { k: "facebook", Icon: Facebook }, { k: "instagram", Icon: Instagram },
    { k: "twitter", Icon: Twitter }, { k: "youtube", Icon: Youtube },
  ].filter((s) => social[s.k]);

  const links = [
    { label: t("nav.mission"), href: `${base}#about` },
    { label: t("nav.causes"), href: `${base}#causes` },
    { label: t("nav.campaigns"), href: `${base}#campaigns` },
    { label: t("nav.wall"), href: `${base}#donor-wall` },
    { label: t("nav.donate"), href: `${base}#donate` },
  ];

  const policyLinks = [
    { label: "Home", href: "/", testid: "footer-link-home" },
    { label: "About Us", href: "/about", testid: "footer-link-about" },
    { label: "Contact Us", href: "/contact", testid: "footer-link-contact" },
    { label: "Privacy Policy", href: "/privacy-policy", testid: "footer-link-privacy" },
    { label: "Terms & Conditions", href: "/terms-and-conditions", testid: "footer-link-terms" },
    { label: "Refund & Cancellation Policy", href: "/refund-policy", testid: "footer-link-refund" },
  ];

  return (
    <footer id="footer" data-testid="site-footer" className="bg-[hsl(160_38%_12%)] text-[hsl(42_33%_96%)] pt-24 pb-28 md:pb-10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-secondary mb-6">{t("footer.getInTouch")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight max-w-md mb-8">
              {t("footer.title")}
            </h2>
            <a href={`${base}#donate`} data-testid="footer-donate" className="rounded-full bg-primary text-primary-foreground px-8 py-3.5 text-sm font-semibold w-fit inline-block hover:scale-105 transition-transform">
              {t("footer.donateNow")}
            </a>
          </div>

          <div className="lg:col-span-3 lg:col-start-8">
            <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-5">{t("footer.explore")}</p>
            <ul className="space-y-3">
              {links.map((l) => (
                <li key={l.href}><a href={l.href} className="text-white/75 hover:text-secondary transition-colors">{l.label}</a></li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-5">{t("footer.contactCol")}</p>
            <a href={`mailto:${email}`} data-testid="footer-email" className="block text-white/75 hover:text-secondary transition-colors break-all mb-2">{email}</a>
            <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} data-testid="footer-phone" className="block text-white/75 hover:text-secondary transition-colors mb-2">{phone}</a>
            {settings?.ngo_darpan_id && (
              <p data-testid="footer-ngo-darpan" className="text-white/50 text-xs mb-3">NGO Darpan ID: {settings.ngo_darpan_id}</p>
            )}
            <div className="mb-3" />
            {socials.length > 0 && (
              <div className="flex gap-3">
                {socials.map(({ k, Icon }) => (
                  <a key={k} href={social[k]} target="_blank" rel="noreferrer" aria-label={k}
                     className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-secondary hover:text-secondary transition-colors">
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="leading-none select-none">
        <p className="font-serif text-[22vw] text-center text-[hsl(42_33%_96%)]/[0.06] tracking-tighter">Findivine</p>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 border-t border-white/10 text-xs text-white/50 space-y-4">
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
          {policyLinks.map((l) => (
            <a key={l.href} href={l.href} data-testid={l.testid} className="hover:text-secondary transition-colors whitespace-nowrap">
              {l.label}
            </a>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} Findivine · One Kind Earth Foundation · Divinity in Humanity</p>
          <div className="flex gap-5">
            <a href="/admin" data-testid="footer-admin-link" className="hover:text-secondary transition-colors">{t("footer.admin")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
